import { formatCurrency } from "../config/market";

const REQUIREMENTS = new Set([
  "NONE",
  "ANY_ONLINE",
  "MOMO",
  "AIRTEL",
  "VISA",
  "ZAMTEL",
]);

export const normaliseProductPaymentRequirement = (item) => {
  const value = String(item?.requiredPaymentMethod || "").trim().toUpperCase();
  if (REQUIREMENTS.has(value)) return value;
  return item?.codDisabled === true ? "ANY_ONLINE" : "NONE";
};

const normaliseCheckoutMethod = (value) => {
  const key = String(value || "").trim().toUpperCase();
  return key === "CARD" ? "VISA" : key;
};

export const resolveProductPaymentPolicy = (
  items = [],
  options = [],
  { codMaxProductAmount = null } = {}
) => {
  /* The platform COD ceiling is a payment requirement like any other, and the
     server has always enforced it: productPaymentPolicyService promotes any
     line whose UNIT price is above the live threshold to ANY_ONLINE and answers
     PAYMENT_METHOD_NOT_ALLOWED. This client mirrored codDisabled and
     requiredPaymentMethod but not the ceiling, so an expensive item let the
     shopper choose Cash on delivery, fill in an address, reach the confirm
     screen and only THEN be refused — with the refusal arriving as a backend
     error message at the last step of the funnel. Mirroring the rule here puts
     the answer on the screen where the choice is made.

     codMaxProductAmount is null until the live value has been read from
     /api/cod-policy, and null means DO NOT APPLY. That is deliberate rather
     than lazy: the threshold is admin-editable at runtime, so guessing it from
     a shipped constant risks hiding Cash on delivery from an order that
     genuinely qualifies — a worse failure than the one being fixed, because it
     is invisible. A failed read leaves behaviour exactly as it was and the
     server stays the backstop. */
  const ceiling = Number(codMaxProductAmount);
  const ceilingActive = Number.isFinite(ceiling) && ceiling > 0;
  const overCeiling = (item) =>
    ceilingActive &&
    normaliseProductPaymentRequirement(item) === "NONE" &&
    Number(item?.price) > ceiling;
  const requirementFor = (item) =>
    overCeiling(item) ? "ANY_ONLINE" : normaliseProductPaymentRequirement(item);
  const ceilingTriggered = items.some(overCeiling);

  const requirements = [
    ...new Set(
      items.map(requirementFor).filter((value) => value !== "NONE")
    ),
  ];
  const specific = requirements.filter((value) => value !== "ANY_ONLINE");
  const conflict = new Set(specific).size > 1;
  const requiredMethod = specific[0] || (requirements.length ? "ANY_ONLINE" : "NONE");
  const filtered = conflict
    ? []
    : options.filter((option) => {
        const method = normaliseCheckoutMethod(option?.key ?? option?.value);
        if (requiredMethod === "NONE") return true;
        if (requiredMethod === "ANY_ONLINE") return method !== "COD";
        return method === requiredMethod;
      });
  const unavailable = !conflict && requiredMethod !== "NONE" && filtered.length === 0;
  const label = {
    MOMO: "MTN Mobile Money",
    AIRTEL: "Airtel Money",
    VISA: "card",
    ZAMTEL: "Zamtel Kwacha",
  }[requiredMethod];

  return {
    options: filtered,
    conflict,
    unavailable,
    requiredMethod,
    notice: conflict
      ? "These items require different payment methods. Order them separately."
      : unavailable
        ? "The required payment method is not available in your current market."
        : requiredMethod === "ANY_ONLINE"
          ? ceilingTriggered
            ? `Cash on delivery isn't available on orders containing an item over ${formatCurrency(
                ceiling
              )}. Please pay online.`
            : "This order must be paid online before the vendor can accept it."
          : label
            ? `This order must be paid with ${label} before the vendor can accept it.`
            : "",
  };
};

